import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { ReactNode } from 'react';
import { CloseIcon } from './icons';

interface BaseDialogProps extends Omit<DialogProps, 'title'> {
  title?: ReactNode;
  titleSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  paperSx?: SxProps<Theme>;
  showCloseButton?: boolean;
  children: ReactNode;
  extraAction?: ReactNode;
  dialogActions?: ReactNode;
}

const BaseDialog = ({
  title,
  titleSx,
  contentSx,
  paperSx,
  children,
  extraAction,
  showCloseButton = true,
  dialogActions,
  ...dialogProps
}: BaseDialogProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const defaultPaperSx = {
    py: 4,
    px: 0,
    pt: showCloseButton ? 1 : 4,
    borderRadius: 2,
    maxWidth: isDesktop ? dialogProps.maxWidth || 400 : '100%',
    [theme.breakpoints.up("md")]: {
      marginLeft: `${theme.mixins.sidebar.width}px`,
    },
    ...paperSx
  };

  return (
    <Dialog
      {...dialogProps}
      fullWidth
      keepMounted={false}
      slotProps={{
        ...dialogProps.slotProps,
        paper: {
          sx: defaultPaperSx,
        },
      }}
    >
      {(showCloseButton || title) && (
        <DialogTitle sx={{ position: 'relative', p: 0, mb: 2, ...titleSx }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
            sx={{
              top: 8,
              right: 24,
              position: 'absolute',
              transform: 'translateX(25%)'
            }}
          >
            {extraAction}
            {showCloseButton && (
              <IconButton
                onClick={(e) => dialogProps.onClose?.(e, 'backdropClick')}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
          <Typography variant="h1" align="center" mt={4}>
            {title}
          </Typography>
        </DialogTitle>
      )}

      <DialogContent sx={{ px: 2, mt: 2, ...contentSx }}>
        {children}
      </DialogContent>

      {dialogActions && (
        <DialogActions>
          {dialogActions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BaseDialog; 